// Job-costing API actions.
//
// Routes live under `/job-costing/*` (NOT `/ghl/embed/job-costing/*`) behind the
// `ghl.sso` middleware; the location is derived from the verified SSO context.
// Backend responses are snake_case Spatie collections; everything is mapped to the
// camelCase shapes in ./types here so components stay clean.
import { Api } from 'Utils/api';
import type {
  JobProfitability,
  CostLine,
  CostCategoryMapping,
  SaveCategoryMappingPayload,
  JobBudget,
  SaveJobBudgetsPayload,
  Paginated,
  PaginationMeta,
  Pipeline,
} from './types';

const getHeaders = (ssoToken: string) => ({ 'X-GHL-SSO': ssoToken });

// ---- mappers ----------------------------------------------------------------

interface RawMeta {
  current_page?: number;
  last_page?: number;
  per_page?: number;
  total?: number;
}

const mapMeta = (meta: RawMeta | undefined, fallbackCount: number): PaginationMeta => ({
  currentPage: meta?.current_page ?? 1,
  lastPage: meta?.last_page ?? 1,
  perPage: meta?.per_page ?? fallbackCount,
  total: meta?.total ?? fallbackCount,
});

const toNumberOrNull = (v: unknown): number | null =>
  (v === null || v === undefined || v === '' ? null : Number(v));

const mapProfitability = (r: Record<string, any>): JobProfitability => ({
  opportunityId: r.opportunity_id,
  name: r.name ?? null,
  rpJobNumber: r.rp_job_number ?? null,
  address: r.address ?? null,
  pipelineId: r.pipeline_id ?? null,
  pipelineStageId: r.pipeline_stage_id ?? null,
  status: r.status ?? null,
  revenue: Number(r.revenue ?? 0),
  totalCost: Number(r.total_cost ?? 0),
  grossProfit: Number(r.gross_profit ?? 0),
  grossMarginPct: toNumberOrNull(r.gross_margin_pct),
  invoiced: Number(r.invoiced ?? 0),
  paid: Number(r.paid ?? 0),
  outstandingAr: Number(r.outstanding_ar ?? 0),
  revenueBasis: r.revenue_basis ?? 'sold',
  costsByCategory: r.costs_by_category ?? {},
  hasBudget: Boolean(r.has_budget ?? false),
  totalBudget: Number(r.total_budget ?? 0),
  budgetVariance: Number(r.budget_variance ?? 0),
  budgetVariancePct: toNumberOrNull(r.budget_variance_pct),
  budgetsByCategory: r.budgets_by_category ?? {},
  varianceByCategory: r.variance_by_category ?? {},
});

const mapBudget = (r: Record<string, any>): JobBudget => ({
  id: String(r.id),
  opportunityId: r.opportunity_id,
  projectId: r.project_id ?? null,
  costCategory: r.cost_category,
  budgetedAmount: Number(r.budgeted_amount ?? 0),
  source: r.source ?? 'manual',
  notes: r.notes ?? null,
});

const mapCostLine = (r: Record<string, any>): CostLine => ({
  id: String(r.id),
  opportunityId: r.opportunity_id,
  projectId: r.project_id ?? null,
  costCategory: r.cost_category,
  source: r.source,
  amount: toNumberOrNull(r.amount),
  txnDate: r.txn_date ?? null,
  vendorName: r.vendor_name ?? null,
  description: r.description ?? null,
  quickbooksTxnType: r.quickbooks_txn_type ?? null,
  matchConfidence: r.match_confidence ?? null,
  matchedBy: r.matched_by ?? null,
});

const mapCategory = (r: Record<string, any>): CostCategoryMapping => ({
  id: String(r.id),
  locationId: r.location_id,
  qboAccountId: r.qbo_account_id ?? null,
  qboAccountName: r.qbo_account_name ?? null,
  qboItemId: r.qbo_item_id ?? null,
  costCategory: r.cost_category,
  createdAt: r.created_at ?? null,
  updatedAt: r.updated_at ?? null,
});

// ---- profitability ----------------------------------------------------------

export interface ProfitabilityQuery {
  pipelineId?: string;
  pipelineStageId?: string;
  status?: string;
  sort?: string;
  page?: number;
  limit?: number;
}

const profitabilityParams = (q: ProfitabilityQuery): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  if (q.pipelineId) params['filter[pipeline_id]'] = q.pipelineId;
  if (q.pipelineStageId) params['filter[pipeline_stage_id]'] = q.pipelineStageId;
  if (q.status) params['filter[status]'] = q.status;
  if (q.sort) params.sort = q.sort;
  if (q.page) params.page = q.page;
  params.limit = q.limit ?? 50;
  return params;
};

export const getProfitability = async (
  ssoToken: string,
  query: ProfitabilityQuery = {}
): Promise<Paginated<JobProfitability>> => {
  const { data } = await Api.get('/job-costing/profitability', {
    params: profitabilityParams(query),
    headers: getHeaders(ssoToken),
  });
  const rows = (data?.data ?? []).map(mapProfitability);
  return { data: rows, meta: mapMeta(data?.meta, rows.length) };
};

export const getJobProfitability = async (
  ssoToken: string,
  opportunityId: string
): Promise<JobProfitability> => {
  const { data } = await Api.get(`/job-costing/profitability/${opportunityId}`, {
    headers: getHeaders(ssoToken),
  });
  return mapProfitability(data?.data ?? data);
};

// ---- cost lines -------------------------------------------------------------

export interface CostLineQuery {
  costCategory?: string;
  source?: string;
  sort?: string;
  page?: number;
  limit?: number;
}

export const getCostLines = async (
  ssoToken: string,
  opportunityId: string,
  query: CostLineQuery = {}
): Promise<Paginated<CostLine>> => {
  const params: Record<string, string | number> = {};
  if (query.costCategory) params['filter[cost_category]'] = query.costCategory;
  if (query.source) params['filter[source]'] = query.source;
  if (query.sort) params.sort = query.sort;
  if (query.page) params.page = query.page;
  params.limit = query.limit ?? 100;

  const { data } = await Api.get(`/job-costing/${opportunityId}/cost-lines`, {
    params,
    headers: getHeaders(ssoToken),
  });
  const rows = (data?.data ?? []).map(mapCostLine);
  return { data: rows, meta: mapMeta(data?.meta, rows.length) };
};

export const getUnassignedCosts = async (
  ssoToken: string,
  page = 1,
  limit = 100
): Promise<Paginated<CostLine>> => {
  const { data } = await Api.get('/job-costing/unassigned-costs', {
    params: { page, limit },
    headers: getHeaders(ssoToken),
  });
  const rows = (data?.data ?? []).map(mapCostLine);
  return { data: rows, meta: mapMeta(data?.meta, rows.length) };
};

// ---- category mappings ------------------------------------------------------

export const listCategories = async (
  ssoToken: string,
  page = 1,
  limit = 50
): Promise<Paginated<CostCategoryMapping>> => {
  const { data } = await Api.get('/job-costing/categories', {
    params: { page, limit },
    headers: getHeaders(ssoToken),
  });
  const rows = (data?.data ?? []).map(mapCategory);
  return { data: rows, meta: mapMeta(data?.meta, rows.length) };
};

const categoryBody = (payload: SaveCategoryMappingPayload): Record<string, unknown> => ({
  qbo_account_id: payload.qboAccountId,
  qbo_account_name: payload.qboAccountName,
  qbo_item_id: payload.qboItemId,
  cost_category: payload.costCategory,
});

export const createCategory = async (
  ssoToken: string,
  payload: SaveCategoryMappingPayload
): Promise<CostCategoryMapping> => {
  const { data } = await Api.post('/job-costing/categories', categoryBody(payload), {
    headers: getHeaders(ssoToken),
  });
  return mapCategory(data?.data ?? data);
};

export const updateCategory = async (
  ssoToken: string,
  categoryId: string,
  payload: SaveCategoryMappingPayload
): Promise<CostCategoryMapping> => {
  const { data } = await Api.put(`/job-costing/categories/${categoryId}`, categoryBody(payload), {
    headers: getHeaders(ssoToken),
  });
  return mapCategory(data?.data ?? data);
};

export const deleteCategory = async (ssoToken: string, categoryId: string): Promise<void> => {
  // Api.delete forwards arg 2 as the axios config (arg 3 is ignored), so headers
  // must go here — matching the other GHL delete actions.
  await Api.delete(`/job-costing/categories/${categoryId}`, { headers: getHeaders(ssoToken) });
};

// ---- budgets (MONGOOSE-FR-007) ----------------------------------------------

export const getJobBudgets = async (
  ssoToken: string,
  opportunityId: string
): Promise<JobBudget[]> => {
  const { data } = await Api.get(`/job-costing/${opportunityId}/budgets`, {
    headers: getHeaders(ssoToken),
  });
  return (data?.data ?? []).map(mapBudget);
};

export const saveJobBudgets = async (
  ssoToken: string,
  opportunityId: string,
  budgets: SaveJobBudgetsPayload
): Promise<JobBudget[]> => {
  const { data } = await Api.put(
    `/job-costing/${opportunityId}/budgets`,
    { budgets },
    { headers: getHeaders(ssoToken) }
  );
  return (data?.data ?? []).map(mapBudget);
};

export const deleteJobBudget = async (
  ssoToken: string,
  opportunityId: string,
  category: string
): Promise<void> => {
  // Headers go in arg 2 (axios config) — matching the other GHL delete actions.
  await Api.delete(`/job-costing/${opportunityId}/budgets/${category}`, {
    headers: getHeaders(ssoToken),
  });
};

// ---- report export (WEBAPP-FR-016) -----------------------------------------

export const getJobCostingReportBlob = async (
  ssoToken: string,
  opportunityId: string
): Promise<Blob> => {
  const { data } = await Api.get(`/job-costing/${opportunityId}/report`, {
    headers: getHeaders(ssoToken),
    responseType: 'blob',
  });
  return data as Blob;
};

// ---- pipelines (for filter dropdowns) — reuses the shared ghl/embed route ----

export const getPipelines = async (ssoToken: string): Promise<Pipeline[]> => {
  const { data } = await Api.get('/ghl/embed/pipelines', { headers: getHeaders(ssoToken) });
  let items: { id: string; name: string; stages?: { id: string; name: string }[] }[] = [];
  if (Array.isArray(data?.data)) items = data.data;
  else if (Array.isArray(data)) items = data;
  return items.map((p) => ({
    id: p.id,
    name: p.name,
    stages: (p.stages || []).map((s) => ({ id: s.id, name: s.name })),
  }));
};
